import { i18n } from "@/plugin/vue-i18n";
import { useAppStore } from "@/store/app";
import {
  FIELDS_ALLOWED_SAME_SLIDE,
  TERMS_AND_CONDITIONS_LABEL,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import { resolveParentFolderId } from "@/util/customFieldFolder";
import {
  autoAddPrimaryField,
  handleContactFieldDrop,
  hasContactFields,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
  validateMixedCustomObjectTypes,
} from "@/util/customObjectUtils";
import { defaultAddressSettings, defaultPaymentSettings } from "@/util/default";
import {
  addressFields,
  applyBusinessStandardFieldPrefix,
  configureRatingElement,
  convertTextboxListOptionsForCustomObjectOnDrop,
  getConstructedQueryKey,
  getDateTimeGeneratedFieldNameBase,
  normalizeContactStandardFieldDrop,
  standardFields,
} from "@/util/methods";
import { normalizePictureChoiceElement } from "@/util/pictureChoice";
import { cloneDeep } from "lodash";
import { v4 as uuid4 } from "uuid";

const { t } = i18n.global;

export const useSurveyDragDropOperations = () => {
  const store = useAppStore();

  const assignSequentialLabelAndKey = (elementDrop: any) => {
    if (elementDrop.tag === "image") {
      const existingImageCount = store.slides.reduce((count, slide) => {
        return (
          count +
          (Array.isArray(slide.slideData)
            ? slide.slideData.filter((el) => el?.tag === "image").length
            : 0)
        );
      }, 0);
      const nextImageIndex = existingImageCount + 1;
      elementDrop.hiddenFieldQueryKey = `image_${nextImageIndex}`;
      elementDrop.label = t("survey.dragDrop.imageLabel", { n: nextImageIndex });
    }

    if (elementDrop.tag === "header") {
      const existingHeaderCount = store.slides.reduce((count, slide) => {
        return (
          count +
          (Array.isArray(slide.slideData)
            ? slide.slideData.filter((el) => el?.tag === "header").length
            : 0)
        );
      }, 0);
      const nextHeaderIndex = existingHeaderCount + 1;
      elementDrop.hiddenFieldQueryKey = `header_${nextHeaderIndex}`;
    }

    if (elementDrop.tag === "html") {
      const existingHtmlCount = store.slides.reduce((count, slide) => {
        return (
          count +
          (Array.isArray(slide.slideData)
            ? slide.slideData.filter((el) => el?.tag === "html").length
            : 0)
        );
      }, 0);
      const nextHtmlIndex = existingHtmlCount + 1;
      elementDrop.hiddenFieldQueryKey = `html_${nextHtmlIndex}`;
      elementDrop.label = t("survey.dragDrop.htmlLabel", { n: nextHtmlIndex });
    }
  };

  const changeLog = async (e: any, slideIndex: number) => {
    const item = e.added?.element || e.moved?.element || e.removed?.element;
    let elementDrop: any = {
      ...cloneDeep(item),
      hiddenFieldQueryKey: item.id ? item.hiddenFieldQueryKey : item.tag,
      active: false,
    };

    // Centralized normalization for standard drops
    elementDrop = applyBusinessStandardFieldPrefix(elementDrop);
    elementDrop = normalizeContactStandardFieldDrop(elementDrop);
    elementDrop = normalizePictureChoiceElement(elementDrop);

    if (e.added) {
      setTimeout(() => {
        if (
          store.slides[slideIndex].slideData.filter((element) => element.model === "opportunity")
            ?.length === 1
        ) {
          store.showOpportunitiesModal = true;
        }

        // Trigger contact association modal when contact field is dropped and custom objects exist
        handleContactFieldDrop(elementDrop, store, {
          slides: store.slides,
        });

        // Trigger contact association modal for true custom objects (exclude business/opportunity)
        if (
          store.slides[slideIndex].slideData.filter((element) =>
            isTrueCustomObjectField(element.model)
          )?.length === 1 &&
          !store.contactAssociationSettings &&
          hasContactFields(null, store.slides) && // Only show modal if there are contact fields
          !store.slides
            .flatMap((s) => s.slideData || [])
            .some((e) => e.model === "business" || e.model === "opportunity")
        ) {
          // Find the custom objects element and store its model
          const customObjectElement = store.slides[slideIndex].slideData.find((element) =>
            isTrueCustomObjectField(element.model)
          );
          if (customObjectElement) {
            store.currentCustomObjectModel = customObjectElement.model;
            store.showContactAssociationModal = true;
          }
        }
        // If this is the first associated object field, add primary field automatically
        else if (
          store.slides[slideIndex].slideData.filter((element) => isCustomObjectField(element.model))
            ?.length === 1 &&
          !store.contactAssociationSettings &&
          (!hasContactFields(null, store.slides) || elementDrop.model === "business")
        ) {
          // Auto-add primary field without showing modal
          autoAddPrimaryField(elementDrop.model, store, {
            slides: store.slides,
            slideIndex: slideIndex,
            addedIndex: e.added.newIndex,
          });
        }

        // Validation for mixed custom object types
        const validationResult = validateMixedCustomObjectTypes(
          elementDrop,
          null, // elements (not used for surveys)
          store.slides,
          slideIndex,
          e.added.newIndex,
          store.customObjects || [],
          (key: string) =>
            key === "common.customObjectsFallback" ? t("common.customObjectsFallback") : key
        );

        if (!validationResult.isValid && validationResult.errorInfo) {
          store.mixedCustomObjectErrorInfo = validationResult.errorInfo as any;
          store.showMixedCustomObjectErrorModal = true;
          // Exit early to prevent further processing
        }
      }, 1000);
      setTimeout(async () => {
        let isAlreadyPresent = false;
        let customElementCount = 0;
        store.slides.forEach((slide) => {
          slide.slideData.forEach((element) => {
            const isSameId = elementDrop?.id && element?.id === elementDrop?.id;
            const isSameTag = !elementDrop?.id && element.tag === elementDrop.tag;
            if (
              element.tag === "group_address" &&
              store.addressSettings.children.find((ele) => ele.tag === elementDrop.tag)
            ) {
              isAlreadyPresent = true;
            }
            const isCustomOrDuplicateAllowed =
              elementDrop?.custom || store.duplicateAllowed.includes(elementDrop.tag);
            if ((isSameId || isSameTag) && !isCustomOrDuplicateAllowed) {
              isAlreadyPresent = true;
            }
            if (element.custom) {
              customElementCount += 1;
            }
          });
        });

        if (!isAlreadyPresent && e.added) {
          if (elementDrop?.custom) {
            if (!store.parentFolderId) {
              store.parentFolderName = t("survey.dragDrop.defaultFolderName", {
                name: store.builder.name,
              });
              store.parentFolderId = await resolveParentFolderId(
                store.locationId,
                store.parentFolderName
              );
            }
            const uniqueKey = UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
              ? UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
              : "";
            const generatedLabelBase =
              (!elementDrop?.id && !elementDrop?.fieldKey
                ? getDateTimeGeneratedFieldNameBase(elementDrop.dataType)
                : undefined) ||
              elementDrop.label;
            const existingElementsCount = [
              ...store.customFields,
              ...store.slides[slideIndex].slideData,
            ].filter((element) => element.dataType === elementDrop.dataType)?.length;
            elementDrop.label = `${generatedLabelBase}${uniqueKey ? ` ${uniqueKey} ` : " "}${
              existingElementsCount + 1
            }${Math.random().toString(36).substr(2, 3)}`;
            elementDrop.hiddenFieldQueryKey = getConstructedQueryKey(elementDrop.label);
            elementDrop.parentId = store.parentFolderId;
          }
          delete elementDrop.icon;
          delete elementDrop.category;

          // Drop-only conversion for custom object TEXTBOX_LIST (via helper)
          elementDrop = convertTextboxListOptionsForCustomObjectOnDrop(
            elementDrop,
            isCustomObjectField(elementDrop?.model)
          );

          // If this is a primary field, make it required by default
          const maybePrimary = isPrimaryField(elementDrop, store.customObjects || []);
          if (maybePrimary.isPrimary) {
            elementDrop.required = true;
          }

          elementDrop = configureRatingElement(elementDrop);
          assignSequentialLabelAndKey(elementDrop);

          elementDrop.uuid = uuid4();
          let targetSlideIndex = slideIndex;
          const targetElementIndex = e.added.newIndex;
          let insertedElement: any = null;

          if (
            store.slides[slideIndex].slideData.find((element) => element.tag === "group_address") &&
            addressFields.find((element) => element.tag === elementDrop.tag)
          ) {
            store.addressSettings.children.push(elementDrop);
          } else if (
            store.formAction.fieldSettingEnable &&
            store.slides[slideIndex].slideData.filter(
              (field) => !FIELDS_ALLOWED_SAME_SLIDE.includes(field.type)
            ).length >= 1 &&
            !FIELDS_ALLOWED_SAME_SLIDE.includes(elementDrop.type)
          ) {
            const nextSlideIndex = slideIndex;
            addSlideBelow(slideIndex);
            targetSlideIndex = nextSlideIndex + 1;
            store.slides[targetSlideIndex].slideData.splice(targetElementIndex, 0, elementDrop);
            insertedElement = store.slides[targetSlideIndex].slideData[targetElementIndex];
          } else {
            store.slides[slideIndex].slideData.splice(targetElementIndex, 0, elementDrop);
            insertedElement = store.slides[slideIndex].slideData[targetElementIndex];
          }

          const termsAndConditionsQuery = (element: any) => element.tag === "terms_and_conditions";
          const privacyElementQuery = (element: any) => element.label === privacyElement.label;
          const textElement = standardFields.customized.group[0];
          const privacyElement = {
            ...textElement,
            fontSize: 14,
            padding: { ...textElement.padding, top: 15 },
            label: t("survey.dragDrop.defaultTermsHtml"),
          };
          const termsAndConditionstPhoneLabel = `<p style='font-family: Roboto; font-size: 14px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'>${TERMS_AND_CONDITIONS_LABEL.PHONE}</p>`;

          const termsAndConditionPromotionalLabel = `<p style='font-family: Roboto; font-size: 14px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'>${TERMS_AND_CONDITIONS_LABEL.PROMOTIONAL}</p>`;
          const preview = `${termsAndConditionstPhoneLabel} | ${termsAndConditionPromotionalLabel}`;
          if (
            elementDrop.tag === "phone" &&
            !store.slides.some((slide) => slide.slideData.find(termsAndConditionsQuery))
          ) {
            store.slides[slideIndex].slideData.push({
              ...standardFields.customized.group.find(termsAndConditionsQuery),
              placeholder: termsAndConditionstPhoneLabel,
              placeholder2: termsAndConditionPromotionalLabel,
              preview: preview,
              uuid: uuid4(),
            });
            if (!store.slides.some((slide) => slide.slideData.find(privacyElementQuery))) {
              store.slides[slideIndex].slideData.push({
                ...privacyElement,
                uuid: uuid4(),
              });
            }
          }
          if (elementDrop.tag === "payment") {
            store.formAction.actionType = "3";
            store.payment = {
              ...defaultPaymentSettings,
              paymentType: elementDrop.subType === "product" ? "product" : "donation",
            };
            store.builderConfig.enablePartialContactCreation = true;
          }
          if (elementDrop.tag === "group_address") {
            store.addressSettings = defaultAddressSettings;
            store.addressSettings.children = addressFields.map((childElement) => {
              const currentChildElementIndex = store.slides[slideIndex].slideData.findIndex(
                (element) => element.tag === childElement.tag
              );
              const currentChildElement =
                currentChildElementIndex === -1
                  ? {}
                  : store.slides[slideIndex].slideData[currentChildElementIndex];
              if (currentChildElementIndex !== -1) {
                store.slides[slideIndex].slideData.splice(currentChildElementIndex, 1);
              }
              return {
                ...childElement,
                ...currentChildElement,
              };
            });
            console.log(store.addressSettings);
          }

          if (insertedElement) {
            store.currentActiveIndex = [targetSlideIndex, targetElementIndex];
            store.currentSlideIndex = targetSlideIndex;
            store.sidebarOverlaySettings = {
              index: targetElementIndex,
              slideIndex: targetSlideIndex,
              formIndex: undefined,
            } as any;
            store.isAnyElementActive = true;
          }
          if (
            elementDrop.model === "opportunity" &&
            store.opportunitySettings.payload.customFields.length
          ) {
            store.opportunitySettings.payload.customFields.push({
              id: elementDrop.Id,
              key: elementDrop.fieldKey.replace("opportunity.", ""),
            });
          }
        }
        store.addToHistory();
      }, 200);
    }

    if (e.removed) {
      store.slides[slideIndex].slideData.splice(e.removed.oldIndex, 1);
      store.addToHistory();
    }
    if (e.moved) {
      const oldIndex = e.moved.oldIndex;
      const newIndex = e.moved.newIndex;

      // Handle the case where we're moving to the same position (no-op)
      if (oldIndex === newIndex) {
        return;
      }

      const draggedElement = store.slides[slideIndex].slideData[oldIndex];
      const newSlideData = [...store.slides[slideIndex].slideData];
      newSlideData.splice(oldIndex, 1);
      newSlideData.splice(newIndex, 0, draggedElement);
      store.slides[slideIndex].slideData = newSlideData;
      // Update the active element index if the moved element was in this slide
      if (store.currentActiveIndex[0] === slideIndex) {
        const currentActiveIndex = store.currentActiveIndex[1];

        // If the dragged element was active, update to new index
        if (currentActiveIndex === oldIndex) {
          store.currentActiveIndex = [slideIndex, newIndex];
          store.currentSlideIndex = slideIndex;
          store.sidebarOverlaySettings = {
            index: newIndex,
            slideIndex: slideIndex,
            formIndex: undefined,
          } as any;
        }
        // If element before active moved past it, adjust active index down
        else if (oldIndex < currentActiveIndex && newIndex >= currentActiveIndex) {
          store.currentActiveIndex = [slideIndex, currentActiveIndex - 1];
          store.currentSlideIndex = slideIndex;
          store.sidebarOverlaySettings = {
            index: currentActiveIndex - 1,
            slideIndex: slideIndex,
            formIndex: undefined,
          } as any;
        }
        // If element after active moved before it, adjust active index up
        else if (oldIndex > currentActiveIndex && newIndex <= currentActiveIndex) {
          store.currentActiveIndex = [slideIndex, currentActiveIndex + 1];
          store.currentSlideIndex = slideIndex;
          store.sidebarOverlaySettings = {
            index: currentActiveIndex + 1,
            slideIndex: slideIndex,
            formIndex: undefined,
          } as any;
        }
      }

      store.addToHistory();
    }
    store.anyUnsavedChanges = true;
  };

  const addSlideBelow = (index: number) => {
    const newSlide = {
      slideName: getSuccessorIndex(),
      slideData: [],
      id: new Date().getTime() + "-" + store.slides.length,
    };
    store.slides.splice(index + 1, 0, newSlide);
    store.formAction.fieldPerPage = store.slides.length;
    store.anyUnsavedChanges = true;
  };

  /**
   * Synchronously insert a batch of pre-selected fields into a slide,
   * preserving the invariants the single-drop `changeLog` path applies:
   *
   *  - cloneDeep + business/contact normalization
   *  - TEXTBOX_LIST conversion for custom objects
   *  - `isPrimary` → required flag
   *  - rating configuration, sequential label/key, unique uuid
   *  - per-field label / hiddenFieldQueryKey / parentId for `custom` fields
   *  - duplicate rejection (`isAlreadyPresent`): skip any incoming field whose
   *    id/tag already exists somewhere in slides (race safety net — the
   *    sidebar's `shouldDisable` normally filters these upstream)
   *  - `store.formAction.fieldSettingEnable` one-question-per-slide splitting:
   *    each non-`FIELDS_ALLOWED_SAME_SLIDE` field lands on its own fresh slide
   *  - `autoAddPrimaryField`: first custom-object field in the slide triggers
   *    the primary-field autofill (bulk semantics: no modal — bulk-insert is
   *    a batched action, we don't want to fan modals across N items)
   *
   * Modal side-effects (opportunity confirmation, contact-association, mixed
   * custom-object validation) are intentionally skipped — bulk insert would
   * fan them out unpredictably. Active-index and history are updated once at
   * the end. If the bulk includes zero effective inserts (all filtered), we
   * return without touching active state or history.
   */
  const bulkAddToSlide = async (
    fields: any[],
    slideIndex: number,
    startIdx: number
  ): Promise<void> => {
    if (!fields || fields.length === 0) {
      return;
    }
    const targetSlide = store.slides?.[slideIndex];
    if (!targetSlide) {
      return;
    }
    if (!Array.isArray(targetSlide.slideData)) {
      return;
    }

    const needsFolder =
      !store.parentFolderId && fields.some((f) => f?.custom);
    if (needsFolder) {
      store.parentFolderName = t("survey.dragDrop.defaultFolderName", {
        name: store.builder.name,
      });
      store.parentFolderId = await resolveParentFolderId(
        store.locationId,
        store.parentFolderName
      );
    }

    // isAlreadyPresent set — matches `changeLog`'s duplicate-rejection check.
    // Keyed by id when the field has one (custom fields), else by tag.
    // `duplicateAllowed` and `custom === true` bypass the rejection (custom
    // fields can be dropped multiple times to create fresh instances).
    const isDuplicate = (candidate: any): boolean => {
      if (!candidate) return true;
      const custom = candidate?.custom === true;
      const tag = candidate?.tag;
      const dupAllowed =
        custom || (tag && store.duplicateAllowed.includes(tag));
      if (dupAllowed) return false;
      const candId = candidate?.id;
      return (store.slides || []).some((s: any) =>
        (s?.slideData || []).some((el: any) => {
          if (candId && el?.id && el.id === candId) return true;
          if (!candId && tag && el?.tag === tag) return true;
          return false;
        })
      );
    };

    const oneQuestionPerSlide = Boolean(store.formAction?.fieldSettingEnable);
    let currentSlideIndex = slideIndex;
    let currentSlide = targetSlide;
    // Splice offset relative to `startIdx` for the initial target slide only.
    // Fields routed onto new slides via one-question-per-slide start at 0.
    let cursorInInitialSlide = Math.min(
      Math.max(0, startIdx),
      targetSlide.slideData.length
    );
    let sawInitialInsert = false;
    let lastInsertedSlideIndex = currentSlideIndex;
    let lastInsertedElement: any = null;
    let lastInsertedIdxInSlide = -1;
    // Track whether any custom-object field lands as the *first* such field on
    // the target slide — that's when autoAddPrimaryField should fire.
    let handledFirstCustomObjectField = false;

    for (const item of fields) {
      // Duplicate check — skip silently. shouldDisable in the sidebar should
      // have prevented this, but a race between selection and drop could slip
      // one through.
      if (isDuplicate(item)) {
        continue;
      }

      let elementDrop: any = {
        ...cloneDeep(item),
        hiddenFieldQueryKey: item.id ? item.hiddenFieldQueryKey : item.tag,
        active: false,
      };
      elementDrop = applyBusinessStandardFieldPrefix(elementDrop);
      elementDrop = normalizeContactStandardFieldDrop(elementDrop);

      if (elementDrop?.custom) {
        const uniqueKey = UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
          ? UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
          : "";
        const existingElementsCount = [
          ...store.customFields,
          ...(store.slides || []).flatMap((s: any) => s?.slideData || []),
        ].filter((el: any) => el.dataType === elementDrop.dataType).length;
        elementDrop.label = `${elementDrop.label}${
          uniqueKey ? ` ${uniqueKey} ` : " "
        }${existingElementsCount + 1}${Math.random()
          .toString(36)
          .substr(2, 3)}`;
        elementDrop.hiddenFieldQueryKey = getConstructedQueryKey(
          elementDrop.label
        );
        elementDrop.parentId = store.parentFolderId;
      }
      delete elementDrop.icon;
      delete elementDrop.category;

      elementDrop = convertTextboxListOptionsForCustomObjectOnDrop(
        elementDrop,
        isCustomObjectField(elementDrop?.model)
      );

      const maybePrimary = isPrimaryField(
        elementDrop,
        store.customObjects || []
      );
      if (maybePrimary.isPrimary) {
        elementDrop.required = true;
      }

      elementDrop = configureRatingElement(elementDrop);
      assignSequentialLabelAndKey(elementDrop);
      elementDrop.uuid = uuid4();

      // One-question-per-slide auto-split: if enabled AND the current slide
      // already contains a non-allowed-same-slide field AND this field itself
      // is not allowed-same-slide, drop it on a fresh new slide below.
      const currentSlideHasBlocker = (currentSlide?.slideData || []).some(
        (f: any) => !FIELDS_ALLOWED_SAME_SLIDE.includes(f?.type)
      );
      const dropIsBlocker = !FIELDS_ALLOWED_SAME_SLIDE.includes(
        elementDrop.type
      );
      if (oneQuestionPerSlide && currentSlideHasBlocker && dropIsBlocker) {
        addSlideBelow(currentSlideIndex);
        currentSlideIndex += 1;
        currentSlide = store.slides[currentSlideIndex];
        cursorInInitialSlide = 0;
      }

      const slideDataRef = currentSlide.slideData as any[];
      const insertAt =
        currentSlideIndex === slideIndex && !sawInitialInsert
          ? cursorInInitialSlide
          : slideDataRef.length;
      slideDataRef.splice(insertAt, 0, elementDrop);
      lastInsertedSlideIndex = currentSlideIndex;
      lastInsertedIdxInSlide = insertAt;
      lastInsertedElement = elementDrop;
      if (currentSlideIndex === slideIndex) {
        sawInitialInsert = true;
        cursorInInitialSlide = insertAt + 1;
      }

      if (
        elementDrop?.model === "opportunity" &&
        store.opportunitySettings?.payload?.customFields?.length &&
        typeof elementDrop.fieldKey === "string"
      ) {
        store.opportunitySettings.payload.customFields.push({
          id: elementDrop.Id,
          key: elementDrop.fieldKey.replace("opportunity.", ""),
        });
      }

      // autoAddPrimaryField mirrors changeLog: only the FIRST custom-object
      // field on a slide (when no contact fields exist elsewhere) auto-adds
      // its primary field. Skip the contact-association modal path (that's
      // the "modal side-effect" bulk deliberately avoids).
      if (
        !handledFirstCustomObjectField &&
        isCustomObjectField(elementDrop?.model) &&
        !store.contactAssociationSettings &&
        (!hasContactFields(null, store.slides) ||
          elementDrop.model === "business")
      ) {
        handledFirstCustomObjectField = true;
        autoAddPrimaryField(elementDrop.model, store, {
          slides: store.slides,
          slideIndex: lastInsertedSlideIndex,
          addedIndex: lastInsertedIdxInSlide,
        });
      }
    }

    if (lastInsertedIdxInSlide < 0) {
      // Every field was a duplicate — nothing to activate, don't push history.
      return;
    }

    const finalSlide = store.slides[lastInsertedSlideIndex];
    if (store.quizId) {
      // Quiz's `QuizElementSidebar` reads `store.slides[slideIndex].slideData
      // [elementIndex]` at setup time via `store.getSlideIndexAndElementIndex`,
      // which expects the quiz shape (elementId + slideId, both uuids).
      // Passing the survey shape (numeric indices) makes that setup throw and
      // cancels the sidebar render (checkbox not clearing / row not disabling).
      const stableElementId =
        lastInsertedElement?.uuid || lastInsertedElement?.hiddenFieldQueryKey;
      const stableSlideId = finalSlide?.id ?? lastInsertedSlideIndex;
      store.currentActiveIndex = [
        stableSlideId,
        stableElementId ?? lastInsertedIdxInSlide,
      ];
      store.currentSlideIndex = stableSlideId;
      store.sidebarOverlaySettings = {
        elementId: stableElementId ?? lastInsertedIdxInSlide,
        slideId: stableSlideId,
        formIndex: undefined,
      } as any;
    } else {
      store.currentActiveIndex = [
        lastInsertedSlideIndex,
        lastInsertedIdxInSlide,
      ];
      store.currentSlideIndex = lastInsertedSlideIndex;
      store.sidebarOverlaySettings = {
        index: lastInsertedIdxInSlide,
        slideIndex: lastInsertedSlideIndex,
        formIndex: undefined,
      } as any;
    }
    store.isAnyElementActive = true;

    store.anyUnsavedChanges = true;
    store.addToHistory();
  };

  const getSuccessorIndex = () => {
    return t("survey.slideLabel", { n: store.slides.length + 1 });
  };

  return {
    changeLog,
    bulkAddToSlide,
    addSlideBelow,
    assignSequentialLabelAndKey,
  };
};
